import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { styled, keyframes } from '@mui/material/styles';
import { Box, IconButton, Stack, Typography, useMediaQuery } from '@mui/material';
// components
import Page from '../Page';
import Iconify from '../Iconify';
import StoreBadges from '../StoreBadges';
import Logo from '../Logo';
// config
import { APP_NAME, GRADIENT_CONFIG } from '../../config';

// ---------------------------------------------------------------------
// AuthShell — responsive split.
// Mobile  (<md): single column, Flutter-app style (clean white).
// Desktop (md+): brand panel left + form right (Stripe/Linear style).
// Mobile uses CSS Grid for centering so children can't widen the row.
// Desktop swaps to a 2-column grid.
// ---------------------------------------------------------------------

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50%      { opacity: 0.85; transform: scale(1.08); }
`;

const Root = styled('main')(({ theme }) => ({
  minHeight: '100vh',
  width: '100%',
  maxWidth: '100vw',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  overflowX: 'hidden',
  background: theme.palette.background.paper,
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'minmax(0, 46fr) minmax(0, 54fr)',
  },
}));

// ---------- LEFT brand panel (desktop only) ----------

const BrandPanel = styled('aside')(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('md')]: {
    position: 'relative',
    overflow: 'hidden',
    minHeight: '100vh',
    color: '#fff',
    background: `linear-gradient(160deg, ${GRADIENT_CONFIG.dark} 0%, ${GRADIENT_CONFIG.primary} 60%, ${GRADIENT_CONFIG.tertiary} 100%)`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: theme.spacing(7, 6),
  },
}));

const DotPattern = styled('div')({
  position: 'absolute',
  inset: 0,
  opacity: 0.14,
  backgroundImage:
    'radial-gradient(circle at center, rgba(255,255,255,1) 1px, transparent 1.5px)',
  backgroundSize: '24px 24px',
  WebkitMaskImage:
    'radial-gradient(ellipse 70% 60% at 50% 50%, black 0%, transparent 80%)',
  maskImage:
    'radial-gradient(ellipse 70% 60% at 50% 50%, black 0%, transparent 80%)',
  pointerEvents: 'none',
});

const Glow = styled('div')({
  position: 'absolute',
  width: 520,
  height: 520,
  borderRadius: '50%',
  background: 'rgba(139,92,246,0.45)',
  filter: 'blur(120px)',
  top: -120,
  right: -120,
  animation: `${pulseGlow} 6s ease-in-out infinite`,
  pointerEvents: 'none',
});

const Glow2 = styled('div')({
  position: 'absolute',
  width: 380,
  height: 380,
  borderRadius: '50%',
  background: 'rgba(39,40,255,0.55)',
  filter: 'blur(100px)',
  bottom: -100,
  left: -80,
  pointerEvents: 'none',
});

// ---------- RIGHT form column ----------

const FormSide = styled('section')(({ theme }) => ({
  minWidth: 0, // critical: lets grid cell shrink instead of overflowing
  width: '100%',
  boxSizing: 'border-box',
  padding: theme.spacing(5, 2.5),
  [theme.breakpoints.up('sm')]: { padding: theme.spacing(6, 5) },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8, 8),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const Inner = styled('div')({
  width: '100%',
  maxWidth: 440,
  marginLeft: 'auto',
  marginRight: 'auto',
  boxSizing: 'border-box',
});

const BackButton = styled(IconButton)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: 999,
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  '&:hover': { background: theme.palette.action.hover },
}));

// ---------- helpers ----------

function MobileTopBar({ showBack, onBack }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ mb: 5, display: { md: 'none' } }}
    >
      {showBack ? (
        <BackButton onClick={onBack} aria-label="Back">
          <Iconify icon="eva:arrow-ios-back-fill" width={20} height={20} />
        </BackButton>
      ) : (
        <Box sx={{ width: 40 }} />
      )}
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 800, letterSpacing: '-0.5px', fontSize: '1.1rem' }}
      >
        <Box component="span" sx={{ color: 'primary.main' }}>Data</Box>
        <Box component="span" sx={{ color: 'secondary.main' }}>World</Box>
      </Typography>
      <Box sx={{ width: 40 }} />
    </Stack>
  );
}

MobileTopBar.propTypes = {
  showBack: PropTypes.bool,
  onBack: PropTypes.func,
};

function DesktopTopBar({ showBack, onBack }) {
  if (!showBack) return null;
  return (
    <Box sx={{ display: { xs: 'none', md: 'flex' }, mb: 6 }}>
      <BackButton onClick={onBack} aria-label="Back">
        <Iconify icon="eva:arrow-ios-back-fill" width={20} height={20} />
      </BackButton>
    </Box>
  );
}

DesktopTopBar.propTypes = {
  showBack: PropTypes.bool,
  onBack: PropTypes.func,
};

// ---------------------------------------------------------------------

AuthShell.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.node,
  children: PropTypes.node.isRequired,
  footer: PropTypes.node,
  pageTitle: PropTypes.string,
  backTo: PropTypes.string,
  showBack: PropTypes.bool,
  quote: PropTypes.string,
  attribution: PropTypes.string,
};

export default function AuthShell({
  title,
  subtitle,
  children,
  footer,
  pageTitle,
  backTo,
  showBack = true,
  quote = 'Pay every bill\nin seconds.',
  attribution = 'Trusted by 10,000+ Nigerians',
}) {
  const navigate = useNavigate();
  const onBack = () => (backTo ? navigate(backTo) : navigate(-1));
  const isDesktop = useMediaQuery((t) => t.breakpoints.up('md'));

  return (
    <Page title={pageTitle || title} sx={{ height: 1 }}>
      <Root>
        {/* LEFT — brand panel (desktop only) */}
        <BrandPanel>
          <DotPattern />
          <Glow />
          <Glow2 />

          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ position: 'relative', zIndex: 2 }}>
            <Box
              sx={{
                borderRadius: 2.5,
                display: 'flex',
                boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
              }}
            >
              <Logo disabledLink sx={{ width: 48, height: 48 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.5px', color: '#fff' }}>
              {APP_NAME}
            </Typography>
          </Stack>

          <Box sx={{ position: 'relative', zIndex: 2, maxWidth: 460 }}>
            <Typography
              sx={{
                fontWeight: 800,
                letterSpacing: '-1.5px',
                lineHeight: 1.05,
                whiteSpace: 'pre-line',
                fontSize: { md: '3rem', lg: '3.6rem' },
                color: '#fff',
              }}
            >
              {quote}
            </Typography>
            <Typography
              sx={{
                mt: 3,
                fontSize: '1rem',
                lineHeight: 1.65,
                color: 'rgba(255,255,255,0.72)',
                maxWidth: 380,
              }}
            >
              Bills, airtime, data, cable — your everyday payments in one beautifully simple app.
            </Typography>
          </Box>

          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ position: 'relative', zIndex: 2 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#4ade80',
                boxShadow: '0 0 12px rgba(74, 222, 128, 0.8)',
              }}
            />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
              {attribution}
            </Typography>
          </Stack>
        </BrandPanel>

        {/* RIGHT — form */}
        <FormSide>
          <Inner>
            {isDesktop ? (
              <DesktopTopBar showBack={showBack} onBack={onBack} />
            ) : (
              <MobileTopBar showBack={showBack} onBack={onBack} />
            )}

            <Box sx={{ mb: 4 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.7px',
                  lineHeight: 1.15,
                  fontSize: { xs: '1.7rem', sm: '1.95rem', md: '2.1rem' },
                  color: 'text.primary',
                }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography
                  sx={{
                    mt: 1.25,
                    fontSize: '0.95rem',
                    lineHeight: 1.55,
                    color: 'text.secondary',
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>

            <Box>{children}</Box>

            {footer && <Box sx={{ mt: 4, textAlign: 'center' }}>{footer}</Box>}

            <Box sx={{ pt: 4 }}>
              <StoreBadges caption="Prefer the mobile app? Get DataWorld on:" />
            </Box>

            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={0.75}
              sx={{ pt: 5, pb: 1, display: { md: 'none' } }}
            >
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', background: 'success.main' }} />
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.72rem' }}>
                Secured by {APP_NAME} · NDPR compliant
              </Typography>
            </Stack>
          </Inner>
        </FormSide>
      </Root>
    </Page>
  );
}
